export interface SportType {
  name: string;
  icon: string;
  color: string;
  description?: string;
}

export interface SportsIconMapping {
  [key: string]: SportType;
}

// Default sport type for fallback
export const DEFAULT_SPORT: SportType = {
  name: 'Olahraga',
  icon: 'mdi:sports',
  color: '#6B7280',
  description: 'Olahraga umum',
};

// Sports icon mapping data
export const SPORTS_MAPPING: SportsIconMapping = {
  // Football/Soccer variants
  futsal: {
    name: 'Futsal',
    icon: 'mdi:soccer',
    color: '#10B981',
    description: 'Permainan sepak bola dalam ruangan',
  },
  football: {
    name: 'Football',
    icon: 'mdi:soccer',
    color: '#10B981',
    description: 'Sepak bola',
  },
  'sepak bola': {
    name: 'Sepak Bola',
    icon: 'mdi:soccer',
    color: '#10B981',
    description: 'Sepak bola',
  },
  minisoccer: {
    name: 'Mini Soccer',
    icon: 'mdi:soccer',
    color: '#059669',
    description: 'Sepak bola lapangan kecil',
  },
  'mini-soccer': {
    name: 'Mini Soccer',
    icon: 'mdi:soccer',
    color: '#059669',
    description: 'Sepak bola lapangan kecil',
  },

  // Badminton
  badminton: {
    name: 'Badminton',
    icon: 'game-icons:shuttlecock',
    color: '#F59E0B',
    description: 'Olahraga raket dengan shuttlecock',
  },
  'bulu tangkis': {
    name: 'Bulu Tangkis',
    icon: 'game-icons:shuttlecock',
    color: '#F59E0B',
    description: 'Olahraga raket dengan shuttlecock',
  },

  // Tennis
  tennis: {
    name: 'Tennis',
    icon: 'mdi:tennis',
    color: '#EF4444',
    description: 'Olahraga raket dengan bola tennis',
  },
  tenis: {
    name: 'Tenis',
    icon: 'mdi:tennis',
    color: '#EF4444',
    description: 'Olahraga raket dengan bola tennis',
  },
  'tennis meja': {
    name: 'Tennis Meja',
    icon: 'mdi:table-tennis',
    color: '#DC2626',
    description: 'Ping pong',
  },
  'ping pong': {
    name: 'Ping Pong',
    icon: 'mdi:table-tennis',
    color: '#DC2626',
    description: 'Tennis meja',
  },

  // Basketball
  basketball: {
    name: 'Basketball',
    icon: 'mdi:basketball',
    color: '#F97316',
    description: 'Bola basket',
  },
  'bola basket': {
    name: 'Bola Basket',
    icon: 'mdi:basketball',
    color: '#F97316',
    description: 'Basketball',
  },
  basket: {
    name: 'Basket',
    icon: 'mdi:basketball',
    color: '#F97316',
    description: 'Basketball',
  },

  // Volleyball
  volleyball: {
    name: 'Volleyball',
    icon: 'mdi:volleyball',
    color: '#8B5CF6',
    description: 'Bola voli',
  },
  'bola voli': {
    name: 'Bola Voli',
    icon: 'mdi:volleyball',
    color: '#8B5CF6',
    description: 'Volleyball',
  },
  voli: {
    name: 'Voli',
    icon: 'mdi:volleyball',
    color: '#8B5CF6',
    description: 'Volleyball',
  },

  // Swimming
  swimming: {
    name: 'Swimming',
    icon: 'mdi:swim',
    color: '#06B6D4',
    description: 'Renang',
  },
  renang: {
    name: 'Renang',
    icon: 'mdi:swim',
    color: '#06B6D4',
    description: 'Swimming',
  },

  // Gym/Fitness
  gym: {
    name: 'Gym',
    icon: 'mdi:dumbbell',
    color: '#374151',
    description: 'Fitness dan latihan beban',
  },
  fitness: {
    name: 'Fitness',
    icon: 'mdi:dumbbell',
    color: '#374151',
    description: 'Latihan kebugaran',
  },

  // Martial Arts
  karate: {
    name: 'Karate',
    icon: 'mdi:karate',
    color: '#DC2626',
    description: 'Seni bela diri karate',
  },
  taekwondo: {
    name: 'Taekwondo',
    icon: 'mdi:karate',
    color: '#7C2D12',
    description: 'Seni bela diri taekwondo',
  },
  boxing: {
    name: 'Boxing',
    icon: 'mdi:boxing-glove',
    color: '#991B1B',
    description: 'Tinju',
  },
  tinju: {
    name: 'Tinju',
    icon: 'mdi:boxing-glove',
    color: '#991B1B',
    description: 'Boxing',
  },

  // Other Sports
  golf: {
    name: 'Golf',
    icon: 'mdi:golf',
    color: '#065F46',
    description: 'Olahraga golf',
  },
  cricket: {
    name: 'Cricket',
    icon: 'mdi:cricket',
    color: '#92400E',
    description: 'Olahraga cricket',
  },
  baseball: {
    name: 'Baseball',
    icon: 'mdi:baseball',
    color: '#1D4ED8',
    description: 'Olahraga baseball',
  },
  hockey: {
    name: 'Hockey',
    icon: 'mdi:hockey-sticks',
    color: '#1E40AF',
    description: 'Olahraga hockey',
  },
  'sepak-bola': {
    name: 'Sepak Bola',
    icon: 'mdi:soccer',
    color: '#10B981',
    description: 'Sepak bola',
  },
  running: {
    name: 'Running',
    icon: 'mdi:run',
    color: '#EF4444',
    description: 'Lari',
  },
  padel: {
    name: 'Padel',
    icon: 'mdi:racquetball',
    color: '#065F46',
    description: 'Olahraga padel',
  },
};

// Helper function to get sport info by name
export const getSportInfo = (sportName: string): SportType => {
  const normalizedName = sportName.toLowerCase().trim();
  return SPORTS_MAPPING[normalizedName] || DEFAULT_SPORT;
};

// Helper function to get all unique sports from mapping
export const getAllSportsTypes = (): SportType[] => {
  const seen = new Set<string>();
  const uniqueSports: SportType[] = [];

  Object.values(SPORTS_MAPPING).forEach((sport) => {
    if (!seen.has(sport.name)) {
      seen.add(sport.name);
      uniqueSports.push(sport);
    }
  });

  return uniqueSports.sort((a, b) => a.name.localeCompare(b.name));
};

// Helper function to get icon color
export const getSportIconColor = (sportName: string): string => {
  return getSportInfo(sportName).color;
};

// Helper function to get icon name
export const getSportIcon = (sportName: string): string => {
  return getSportInfo(sportName).icon;
};

// Helper function to check if a sport category supports Sparring mode
export const isSparringSupportedSport = (
  selectedCat?: { slug?: string; name?: string } | null
): boolean => {
  if (!selectedCat) return false;
  const slug = selectedCat.slug?.toLowerCase() || '';

  const supportedSlugs = ['padel', 'mini-soccer', 'sepak-bola', 'futsal'];

  return supportedSlugs.includes(slug);
};

// List of sports that support "Hasil Match" page
export const HASIL_SPORTS_SLUGS = [
  'sepak-bola',
  'tennis',
  'badminton',
  'futsal',
  'mini-soccer',
  'padel',
  'pickleball',
];

/**
 * Helper function to check if a sport category or event supports the "Hasil Match" page
 */
export const hasHasilPage = (sportSlugOrName?: string, isSparring?: boolean): boolean => {
  if (isSparring) return true;
  if (!sportSlugOrName) return false;
  const normalized = sportSlugOrName.toLowerCase().trim().replace(/\s+/g, '-');
  return (
    HASIL_SPORTS_SLUGS.includes(normalized) ||
    HASIL_SPORTS_SLUGS.includes(sportSlugOrName.toLowerCase().trim())
  );
};
